import { ApolloClient, ApolloQueryResult } from '@apollo/client';
import { get } from 'lodash';
import { getPriceByQuery } from '../common/helpers/domain/priceOptions';
import logger from '../common/helpers/logger/logger';
import { SEARCH_SUGGESTION_QUERY } from '../queries/common/searchSuggestion';
import { SEARCH_LOCATION_SUGGESTIONS_QUERY } from '../queries/homePage/searchLocationSuggestions';
import { SEARCH_CONTENT_QUERY } from '../queries/searchPage/searchContent';
import { FREE_TEXT_POSTS_QUERY } from '../queries/searchPage/searchFreetext';
import {
  ProjectQueryData,
  SearchPageAdvanceProjectsQueryVars,
  SearchPageFreeTextProjectsQueryVars,
  SearchPageProjectsQueryVars,
  SEARCH_PAGE_ADVANCE_PROJECT_QUERY,
  SEARCH_PAGE_FREE_TEXT_PROJECT_QUERY,
  SEARCH_PAGE_PROJECTS_QUERY,
} from '../queries/searchPage/searchProjects';
import { PropertyType } from '../common/helpers/domain/propertyTypes';
import { PostSubcategory } from '../common/helpers/domain/postCategories';

interface FetchSearchPostsOptions {
  termsTaxonomy: string;
  termsSlug: string;
  projectSlug: string;
  offset: number;
  perPage: number;
  postCategory?: PostSubcategory[];
  videoType?: string[];
}

export const fetchSearchPosts = async (
  client: ApolloClient<unknown>,
  options: FetchSearchPostsOptions,
) => {
  try {
    logger.logInfo('searchService.fetchSearchPosts', 'Begin', options);

    const variables = {
      termsTaxonomy: options.termsTaxonomy,
      termsSlug: options.termsSlug,
      projectSlug: options.projectSlug,
      offset: options.offset,
      perPage: options.perPage,
      postCategory: options.postCategory,
      videoType: options.videoType,
    };

    const response = await client.query({
      query: SEARCH_CONTENT_QUERY,
      variables: variables,
    });

    return get(response, 'data', {});
  } catch (error) {
    logger.logError(
      'searchService.fetchSearchPosts',
      'Error retrieving from query.',
      {
        options,
        error,
      },
    );
    throw error;
  }
};

interface FetchSearchPostsFreeTextOptions {
  freeText: string;
  offset: number;
  perPage: number;
  postCategory?: PostSubcategory[];
  videoType?: string[];
}

export const fetchSearchPostsFreeText = async (
  client: ApolloClient<unknown>,
  options: FetchSearchPostsFreeTextOptions,
) => {
  try {
    logger.logInfo('searchService.fetchSearchPostsFreeText', 'Begin', options);

    const variables = {
      freeText: options.freeText,
      offset: options.offset,
      perPage: options.perPage,
      postCategory: options.postCategory,
      videoType: options.videoType,
    };

    const response = await client.query({
      query: FREE_TEXT_POSTS_QUERY,
      variables: variables,
    });
    return get(response, 'data', {});
  } catch (error) {
    logger.logError(
      'searchService.fetchSearchPostsFreeText',
      'Error retrieving from query.',
      {
        options,
        error,
      },
    );
    throw error;
  }
};

interface FetchSearchSuggestionsOptions {
  keyword: string;
}

export const fetchSearchSuggestions = async (
  client: ApolloClient<unknown>,
  options: FetchSearchSuggestionsOptions,
) => {
  try {
    logger.logInfo('searchService.fetchSearchSuggestions', 'Begin', options);

    const variables = {
      keyword: options.keyword,
    };

    return await client.query({
      query: SEARCH_SUGGESTION_QUERY,
      variables: variables,
    });
  } catch (error) {
    logger.logError(
      'searchService.fetchSearchSuggestions',
      'Error retrieving from query.',
      error,
    );
    throw error;
  }
};

interface FetchSearchLocationSuggestionsOptions {
  keyword: string;
}

export const fetchSearchLocationSuggestions = async (
  client: ApolloClient<unknown>,
  options: FetchSearchLocationSuggestionsOptions,
) => {
  try {
    logger.logInfo(
      'searchService.fetchSearchLocationSuggestions',
      'Begin',
      options,
    );

    const variables = {
      keyword: options.keyword,
    };

    return await client.query({
      query: SEARCH_LOCATION_SUGGESTIONS_QUERY,
      variables: variables,
    });
  } catch (error) {
    logger.logError(
      'searchService.fetchSearchLocationSuggestions',
      'Error retrieving from query.',
      { options, error },
    );
    throw error;
  }
};

export type ProjectFilterParams = {
  termsTaxonomy: string;
  termsSlug: string;
  slug: string;
  projectSlug: string;
  minPrice: string;
  propertyType: PropertyType;
  maxPrice: string;
  freeText: string;
};

export const queryProjects = async (
  offset: number,
  searchQuery: ProjectFilterParams,
  client: ApolloClient<unknown>,
  perPage: number,
): Promise<ProjectQueryData['projectListCarouselQuery']> => {
  const { minPrice, maxPrice, propertyType, freeText } = searchQuery;
  let queryResult: ApolloQueryResult<ProjectQueryData>;
  if (minPrice || maxPrice || propertyType) {
    // Advanced query when filters are applied
    const propertyTypeQuery = propertyType ?? '';
    const minPriceFromQuery = getPriceByQuery(minPrice);
    const maxPriceFromQuery = getPriceByQuery(maxPrice);

    queryResult = await fetchSearchProjectsAdvanced(client, {
      termsTaxonomy: searchQuery.termsTaxonomy,
      termsSlug: searchQuery.termsSlug,
      slug: searchQuery.slug,
      freeText: searchQuery.freeText,
      // Project price is considered 0 when not specified.
      // Set a small value to ignore them if maxPrice is specified.
      minPrice: minPriceFromQuery
        ? minPriceFromQuery / 1000000
        : maxPriceFromQuery
          ? 0.0001
          : 0,
      maxPrice: maxPriceFromQuery ? maxPriceFromQuery / 1000000 : undefined,
      propertyTypes: [propertyTypeQuery],
      offset,
      perPage,
    });
  } else if (freeText) {
    // Keyword based query
    queryResult = await fetchSearchProjectsFreeText(client, {
      freeText,
      offset,
      perPage,
    });
  } else {
    // Default query when there is no keyword and no filters applied
    queryResult = await fetchSearchProjects(client, {
      termsTaxonomy: searchQuery.termsTaxonomy,
      termsSlug: searchQuery.termsSlug,
      slug: searchQuery.slug,
      offset,
      perPage,
    });
  }

  return queryResult.data.projectListCarouselQuery;
};

interface FetchSearchProjectsOptions {
  termsTaxonomy: string;
  termsSlug: string;
  slug: string;
  offset: number;
  perPage: number;
}

export const fetchSearchProjects = async (
  client: ApolloClient<unknown>,
  options: FetchSearchProjectsOptions,
) => {
  try {
    logger.logInfo('searchService.fetchSearchProjects', 'Begin', options);

    const variables: SearchPageProjectsQueryVars = {
      termsTaxonomy: options.termsTaxonomy,
      termsSlug: options.termsSlug,
      slug: options.slug,
      offset: options.offset,
      perPage: options.perPage,
    };

    return await client.query<ProjectQueryData, SearchPageProjectsQueryVars>({
      query: SEARCH_PAGE_PROJECTS_QUERY,
      variables: variables,
    });
  } catch (error) {
    logger.logError(
      'searchService.fetchSearchProjects',
      'Error retrieving from query.',
      {
        options,
        error,
      },
    );
    throw error;
  }
};

interface FetchSearchProjectsFreeTextOptions {
  freeText: string;
  offset: number;
  perPage: number;
}

export const fetchSearchProjectsFreeText = async (
  client: ApolloClient<unknown>,
  options: FetchSearchProjectsFreeTextOptions,
) => {
  try {
    logger.logInfo(
      'searchService.fetchSearchProjectsFreeText',
      'Begin',
      options,
    );

    const variables: SearchPageFreeTextProjectsQueryVars = {
      freeText: options.freeText,
      offset: options.offset,
      perPage: options.perPage,
    };

    return await client.query<
      ProjectQueryData,
      SearchPageFreeTextProjectsQueryVars
    >({
      query: SEARCH_PAGE_FREE_TEXT_PROJECT_QUERY,
      variables: variables,
    });
  } catch (error) {
    logger.logError(
      'searchService.fetchSearchProjectsFreeText',
      'Error retrieving from query.',
      {
        options,
        error,
      },
    );
    throw error;
  }
};

interface FetchSearchProjectsAdvancedOptions {
  termsTaxonomy: string;
  termsSlug: string;
  slug: string;
  freeText: string;
  minPrice: number;
  maxPrice: number;
  propertyTypes: string[];
  offset: number;
  perPage: number;
}

export const fetchSearchProjectsAdvanced = async (
  client: ApolloClient<unknown>,
  options: FetchSearchProjectsAdvancedOptions,
) => {
  try {
    logger.logInfo(
      'searchService.fetchSearchProjectsAdvanced',
      'Begin',
      options,
    );

    const variables: SearchPageAdvanceProjectsQueryVars = {
      termsTaxonomy: options.termsTaxonomy,
      termsSlug: options.termsSlug,
      slug: options.slug,
      freeText: options.freeText,
      priceMin: options.minPrice,
      priceMax: options.maxPrice,
      propertyType: options.propertyTypes,
      offset: options.offset,
      perPage: options.perPage,
    };

    return await client.query<
      ProjectQueryData,
      SearchPageAdvanceProjectsQueryVars
    >({
      query: SEARCH_PAGE_ADVANCE_PROJECT_QUERY,
      variables: variables,
    });
  } catch (error) {
    logger.logError(
      'searchService.fetchSearchProjectsAdvanced',
      'Error retrieving from query.',
      {
        options,
        error,
      },
    );
    throw error;
  }
};
